<script setup>
import { computed, ref } from 'vue'
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue'
import GeneralButton from '@/Components/Links/GeneralButton.vue'
import CardTitle from '@/Components/Links/CardTitle.vue'
import FeaturedCardIcon from '@/Components/Links/FeaturedCardIcon.vue'
import { buildLinkAssetUrl } from '@/utils/assetUrls.js'

const props = defineProps({
  link: { type: Object, required: true },
  appearance: { type: Object, default: () => ({}) },
  buttonStyle: { type: [Object, Array, String], default: () => ({}) },
  DoBucket: String,
  DoEndpoint: String,
})

const emit = defineEmits(['click', 'share'])

// Local state to control inline preview visibility (for classic + preview-only)
const showPreview = ref(false)

function onClick() {
  const opt = props.link?.options || {}
  const music = opt.music || {}
  const mode = String(music.display_mode || props.link?.music_option || '').toLowerCase()
  const isPreviewOnly = mode === 'preview-only'
  const isClassic = (props.link?.options?.layout || 'classic') === 'classic'

  if (isClassic && isPreviewOnly) {
    // Toggle inline preview instead of opening modal
    showPreview.value = !showPreview.value
    return
  }
  emit('click', props.link)
}
function onShare(e){ e?.stopPropagation?.(); emit('share', props.link) }

import scIcon from '@/assets/icons/platforms/soundcloud.svg'

function isSoundCloud(){
  return (props.link?.platform || '').toLowerCase() === 'soundcloud'
}

const layout = computed(() => props.link?.options?.layout || 'classic')
const coverImageUrl = computed(() =>
  buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, props.link?.file)
)

// Whether the link should go directly to URL (no preview iframe)
const isGoDirectly = computed(() => {
  const opt = props.link?.options || {}
  const music = opt.music || {}
  const mode = music.display_mode || props.link?.music_option
  return String(mode).toLowerCase() === 'go-directly'
})

// Build SoundCloud widget URL when applicable, otherwise use provided preview
const embedSrc = computed(() => {
  const preview = props.link?.streamingService?.preview
  if (!isSoundCloud()) return preview

  // Prefer an explicit track URL if provided, else fallback to preview
  const trackUrl = props.link?.streamingService?.url || props.link?.url || ''
  // If we don't have any better URL, return the preview as-is
  if (!trackUrl) return preview

  const base = 'https://w.soundcloud.com/player/'
  const params = new URLSearchParams({
    url: trackUrl,
    color: '#ff5500',
    auto_play: 'false',
    hide_related: 'false',
    show_comments: 'true',
    show_user: 'true',
    show_reposts: 'false',
    show_teaser: 'true',
    visual: 'true',
  })
  return `${base}?${params.toString()}`
})

// Compute the primary display text for the track meta
const displayPrimary = computed(() => {
  const sc = props.link?.streamingService || {}
  const album = (sc.album || '').trim()
  if (album && album.toLowerCase() !== 'unknown track') return album
  const title = (sc.title || '').trim()
  return title || props.link?.title || 'Unknown Track'
})

defineExpose({
    playInline() {
        showPreview.value = true
    }
})

</script>

<template>
  <!-- FEATURED layout -->
  <GeneralEmbedButton
    v-if="layout==='featured'"
    class="soundcloud-card"
    :link="link"
    :appearance="appearance"
    :buttonStyle="buttonStyle"
    :DoBucket="DoBucket"
    :DoEndpoint="DoEndpoint"
    :layout="layout"
    @click="onClick"
  >
    <!-- Header: icon + centered title between left icon and right share, with spacing -->
    <div class="d-flex align-items-center mb-2" style="height:3rem; padding-top:6px; padding-left:12px; padding-right:12px; gap:8px;">
      <!-- Left SoundCloud Icon: respects icon_display / icon_style settings -->
      <FeaturedCardIcon :link="link" platform="soundcloud" :fallbackIcon="scIcon" :DoBucket="DoBucket" :DoEndpoint="DoEndpoint" />
      <!-- Center Title between icons -->
      <CardTitle :title="link.title" layout="classic" align="center" :max="100" class="flex-grow-1" style="min-width:0" />
      <!-- Share icon on the right -->
      <span class="d-flex align-items-center" aria-label="Share" style="flex-shrink:0">
        <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="SVGRepo_iconCarrier">
            <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                  d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
          </g>
        </svg>
      </span>
    </div>

    <!-- Media area: optional static image for non-SoundCloud, iframe preview for SoundCloud (and other supported) -->
    <div class="text-center">
      <!-- Show static cover image when not SoundCloud OR when 'go-directly' is selected -->
      <div v-if="!isSoundCloud() || isGoDirectly"
           class="mx-auto"
           :style="{
              width: '100%',
              height: '200px',
              borderRadius: '12px 12px 0 0',
              backgroundImage: coverImageUrl ? `url(${coverImageUrl})` : undefined,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
           }"
      ></div>

      <!-- Streaming preview iframe (hidden when 'go-directly') -->
      <iframe
        v-if="link?.streamingService?.preview && !isGoDirectly"
        :src="embedSrc"
        width="100%"
        height="200"
        frameborder="0"
        allow="autoplay; encrypted-media"
        allowfullscreen
      />

      <!-- Track meta -->
      <div class="mt-2 d-none">
        <small class="text-muted">{{ link?.streamingService?.title }}</small>
      </div>
    </div>
  </GeneralEmbedButton>

  <!-- CLASSIC layout -->
  <GeneralButton
    v-else
    class="soundcloud-card"
    :link="link"
    :appearance="appearance"
    :buttonStyle="buttonStyle"
    :DoBucket="DoBucket"
    :DoEndpoint="DoEndpoint"
    :layout="layout"
    :hideLeftImage="showPreview"
    @click="onClick"
  >
    <template #title>
      <CardTitle :title="link.title" :artist="link?.streamingService?.title" layout="classic" :align="showPreview ? 'center' : 'start'" :max="100" />
    </template>
    <template #actions>
      <svg class="share-icon" @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g id="SVGRepo_iconCarrier">
          <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
        </g>
      </svg>
    </template>
    <!-- Inline slide-down preview INSIDE the button for classic + preview-only -->
    <transition name="slide-fade">
      <div v-show="showPreview" class="inline-preview" @click.stop>
        <iframe
          v-if="embedSrc"
          :src="embedSrc"
          width="100%"
          height="200"
          frameborder="0"
          allow="autoplay; encrypted-media"
          allowfullscreen
        />
      </div>
    </transition>
  </GeneralButton>
</template>

<style scoped>
.soundcloud-card :deep(iframe){ border: 0; }

.inline-preview {
  overflow: hidden;
  padding-top: 10px;
}
/* Slide down animation */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
  max-height: 0;
  opacity: 0;
}
.slide-fade-enter-to,
.slide-fade-leave-from {
  max-height: 260px; /* 200 iframe + paddings */
  opacity: 1;
}
</style>
